/**
 * Deriva os atletas de um lado (A/B) de uma partida.
 *
 * Nos modelos de rodízio (REI_RAINHA / SUPER_8) as partidas têm `equipeA`/`equipeB`
 * NULOS — os lados são formados por 2 atletas avulsos em `participantesA`/`participantesB`
 * (via partida_participante). Nos demais modelos os lados são equipes com `atletas`.
 *
 * Este util unifica a leitura: usa a equipe quando presente; senão cai para os
 * participantes. Sem ele, telas que leem só `equipe.atletas` renderizam vazio (ou
 * detectam o lado do atleta errado) no rodízio.
 *
 * Utils são funções puras e sem dependências (ver ai/architecture.core.md §3.5).
 */

export interface AtletaLado {
  id: string;
  nome: string;
  imagemUrl: string | null;
}

interface UsuarioLike {
  nome?: string | null;
  imagemUrl?: string | null;
}

interface AtletaLike {
  id: string;
  usuario?: UsuarioLike | null;
}

/**
 * Tipo de entrada PERMISSIVO/estrutural: casa com os vários tipos de partida do
 * projeto (PartidaLite, MatchCardPartida, tipos inline das páginas etc.) sem exigir
 * um tipo nominal único.
 */
export interface PartidaLadoLike {
  equipeA?: { atletas?: Array<AtletaLike | null> } | null;
  equipeB?: { atletas?: Array<AtletaLike | null> } | null;
  participantesA?: Array<AtletaLike> | null;
  participantesB?: Array<AtletaLike> | null;
}

function normalizar(atleta: AtletaLike): AtletaLado {
  return {
    id: atleta.id,
    nome: atleta.usuario?.nome || 'Atleta',
    imagemUrl: atleta.usuario?.imagemUrl ?? null,
  };
}

/**
 * Atletas de um lado: usa a equipe quando presente; senão cai para os participantes
 * (rodízio). Retorna sempre um array (vazio quando o lado não está definido).
 */
export function atletasDoLado(partida: PartidaLadoLike, lado: 'A' | 'B'): AtletaLado[] {
  const equipe = lado === 'A' ? partida.equipeA : partida.equipeB;
  const atletasEquipe = equipe?.atletas;

  if (atletasEquipe && atletasEquipe.length > 0) {
    return atletasEquipe
      .filter((a): a is AtletaLike => a != null)
      .map(normalizar);
  }

  const participantes = lado === 'A' ? partida.participantesA : partida.participantesB;
  return (participantes ?? []).map(normalizar);
}

/**
 * True se o atleta joga naquele lado (equipe ou participante). Útil para detecção
 * de lado (ex.: destacar o lado do atleta logado).
 */
export function atletaEstaNoLado(
  partida: PartidaLadoLike,
  lado: 'A' | 'B',
  atletaId: string,
): boolean {
  return atletasDoLado(partida, lado).some((atleta) => atleta.id === atletaId);
}

/** Tipo estrutural permissivo para leitura do lado vencedor de uma partida. */
export interface PartidaVencedorLike {
  equipeVencedora?: string | null;
  equipeA?: { id?: string } | null;
  equipeB?: { id?: string } | null;
}

/**
 * Lado vencedor da partida ('A' | 'B') ou null quando não há vencedor definido.
 *
 * O backend persiste `equipeVencedora` como a STRING do lado ("A"/"B") — tanto no
 * placar (`determinarEquipeVencedoraString`) quanto no WO/desistência — e **nunca**
 * o id da equipe. Nos modelos de rodízio (REI_RAINHA/SUPER_8) `equipeA/equipeB` são
 * nulos, então comparar `equipeVencedora` com `equipe?.id` nunca casa. Esta função é
 * a fonte única: compara pelo lado "A"/"B" (cobre rodízio e legado) e mantém um
 * fallback defensivo para eventuais dados legados que tenham gravado o id da equipe.
 */
export function ladoVencedor(partida: PartidaVencedorLike): 'A' | 'B' | null {
  const vencedora = partida.equipeVencedora;
  if (!vencedora) return null;
  if (vencedora === 'A' || vencedora === 'B') return vencedora;
  // Fallback defensivo: dados legados que gravaram o id da equipe em vez de "A"/"B".
  if (partida.equipeA?.id && vencedora === partida.equipeA.id) return 'A';
  if (partida.equipeB?.id && vencedora === partida.equipeB.id) return 'B';
  return null;
}
